
import { Challenge } from '../lib/types';
import { CountdownTimer } from './CountdownTimer';
import { AnimatedBackground } from './AnimatedBackground';
import { AlertCircle, Camera, Timer, Vote } from 'lucide-react';

interface ChallengeStatusProps {
  challenge: Challenge;
  onStatusChange?: () => void;
}

export const ChallengeStatus: React.FC<ChallengeStatusProps> = ({ 
  challenge,
  onStatusChange
}) => {
  const getStatusInfo = () => {
    switch (challenge.status) {
      case 'voting-prompts':
        return {
          icon: <Vote className="h-10 w-10 text-bauhaus-offwhite" />,
          title: 'Vote for Today\'s Prompt',
          description: 'Choose your favorite prompt to photograph today!',
          deadline: challenge.promptVotingEndTime,
          deadlineLabel: 'Voting ends in:',
          bgClass: 'bg-bauhaus-darkgreen'
        };
      case 'submitting':
        return {
          icon: <Camera className="h-10 w-10 text-bauhaus-offwhite" />,
          title: challenge.selectedPrompt ? challenge.selectedPrompt.text : 'Today\'s Challenge',
          description: 'Take your best shot that matches the prompt!',
          deadline: challenge.submissionDeadline,
          deadlineLabel: 'Submission ends in:',
          bgClass: 'bg-bauhaus-green'
        };
      case 'voting-submissions':
        return {
          icon: <Vote className="h-10 w-10 text-bauhaus-offwhite" />,
          title: 'Vote for the Best Photo',
          description: 'Choose your favorite photo submission!',
          deadline: challenge.votingDeadline,
          deadlineLabel: 'Voting ends in:',
          bgClass: 'bg-bauhaus-tan'
        };
      case 'closed':
        return {
          icon: <AlertCircle className="h-10 w-10 text-bauhaus-offwhite" />,
          title: 'Challenge Closed',
          description: 'Today\'s challenge has ended. Check back tomorrow!',
          deadline: '',
          deadlineLabel: '',
          bgClass: 'bg-bauhaus-brown'
        };
      default:
        return {
          icon: <Timer className="h-10 w-10 text-bauhaus-offwhite" />,
          title: 'Loading Challenge...',
          description: 'Please wait...',
          deadline: '',
          deadlineLabel: '',
          bgClass: 'bg-bauhaus-green'
        };
    }
  };
  
  const status = getStatusInfo();
  
  return (
    <div className={`relative overflow-hidden ${status.bgClass} rounded-xl shadow-xl p-6 mb-6 min-h-[280px] border-2 border-bauhaus-offwhite`}>
      <AnimatedBackground density={20} speed={1.5} />
      
      <div className="flex flex-col items-center gap-3 mb-6 relative z-10">
        <div className="bg-bauhaus-darkgreen p-4 rounded-full backdrop-blur-sm shadow-xl">
          {status.icon}
        </div>
        <h2 className="text-2xl font-bold text-bauhaus-offwhite text-center drop-shadow-md font-['Bebas_Neue'] tracking-wider">{status.title}</h2>
        <p className="text-bauhaus-offwhite/90 text-center text-lg font-medium">{status.description}</p>
      </div>
      
      {status.deadline && (
        <div className="mt-4 relative z-10">
          <p className="text-md text-center text-bauhaus-offwhite mb-2 font-medium tracking-wider">{status.deadlineLabel}</p>
          <CountdownTimer 
            targetDate={status.deadline} 
            onComplete={onStatusChange}
            className="mt-3"
          />
        </div>
      )}
    </div>
  );
};
